import type { AuctionUpdates } from '@b-stock/fe-core/bstock-next/helpers'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'

import type { SearchResult } from '@/queries/listingSearchQuery'

export type EntNewBidEvent = {
  auction_id: string
  site_abb: string
  current_bid?: number
  num_bids: number
  winning_user_ua_id: string
  currency_symbol: string
}

/**
 * Applies a 3MP auction update to a SearchResult
 */
export function apply3MPAuctionUpdate(
  listing: SearchResult,
  update: AuctionUpdates
): { updatedListing: SearchResult; bidStatus: BidStatus } {
  let updatedListing = listing

  if (update.type === 'public' || update.type === 'poll') {
    // The 'poll' fallback fetches a full, authenticated auction record that
    // carries the real extension count; the 'public' pusher payload (a
    // LimitedAuction) never does. When it's absent, treat a changed
    // actualEndTime as a single popcorn-bidding time extension so the
    // "(+N popcorn)" label updates immediately instead of only after the
    // next full search refetch.
    const authoritativeNPopcorn = (
      update.data as { extendedBiddingCount?: number }
    ).extendedBiddingCount
    const auctionExtended =
      !update.data.closed &&
      update.data.actualEndTime !== undefined &&
      update.data.actualEndTime !== updatedListing.auction3MP?.actualEndTime

    updatedListing = {
      ...updatedListing,
      // Update top-level numberOfBids for spot listings
      numberOfBids:
        update.data.numberOfBids || updatedListing.numberOfBids || 0,
      nPopcorn:
        authoritativeNPopcorn ??
        (auctionExtended
          ? (updatedListing.nPopcorn ?? 0) + 1
          : updatedListing.nPopcorn),
      // There's no foreseeable situation where the original record won't
      // have an auction field and we get an update here, but TSC wants
      // us to check.
      auction3MP: updatedListing.auction3MP
        ? {
            ...updatedListing.auction3MP,
            ...update.data,
            // Handle nullable price fields
            startPrice:
              update.data.startPrice ||
              updatedListing.auction3MP.startPrice ||
              0,
            winningBidAmount:
              update.data.winningBidAmount ||
              updatedListing.auction3MP.winningBidAmount ||
              0,
            nextMinBidAmount:
              update.data.nextMinBidAmount ||
              updatedListing.auction3MP.nextMinBidAmount ||
              0,
          }
        : null,
    }
  } else if (update.type === 'status') {
    updatedListing = {
      ...updatedListing,
      auction3MP: updatedListing.auction3MP
        ? {
            ...updatedListing.auction3MP,
            bidStatus: update.data.bidStatus,
            closed: update.data.closed ?? updatedListing.auction3MP.closed,
          }
        : null,
    }
    // The linter is correct here, update.type can only be 'private' at this point
    // per the type system. However, I think this is clearer AND if we ever grow a
    // new type of update and miss that fact, this seems safer at runtime
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (update.type === 'private') {
    updatedListing = {
      ...updatedListing,
      auction3MP: updatedListing.auction3MP
        ? {
            ...updatedListing.auction3MP,
            bidStatus: update.data.bidStatus,
            bid: update.data.bid ?? updatedListing.auction3MP.bid,
          }
        : null,
    }
  }

  const bidStatus = updatedListing.auction3MP?.bidStatus ?? BidStatus.NO_BID

  return { updatedListing, bidStatus }
}

/**
 * Applies an Enterprise auction bid update to a SearchResult
 */
export function applyEnterpriseAuctionUpdate(
  listing: SearchResult,
  bidEvent: EntNewBidEvent,
  userUaId: string | null
): SearchResult {
  const hasBid =
    listing.auctionEnt?.bidStatus &&
    listing.auctionEnt.bidStatus !== BidStatus.NO_BID

  let newBidStatus = BidStatus.NO_BID

  if (userUaId === bidEvent.winning_user_ua_id) {
    newBidStatus = BidStatus.WINNING
  } else if (hasBid) {
    // User had bid, but isn't the current winner. That makes them a LOSER
    newBidStatus = BidStatus.LOSING
  }

  return {
    ...listing,
    winningBidAmount: bidEvent.current_bid ?? 0,
    numberOfBids: bidEvent.num_bids,
    auctionEnt: listing.auctionEnt
      ? {
          ...listing.auctionEnt,
          bidStatus: newBidStatus,
          winningBidAmount:
            bidEvent.current_bid ?? listing.auctionEnt.winningBidAmount,
        }
      : null,
  }
}
